import {
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  Spinner,
  useBreakpointValue,
  Select,
  Modal,
  ModalContent,
  ModalOverlay,
  Textarea,
  ModalFooter,
  ModalBody,
  ModalHeader,
  Input,
  useToast,
  IconButton,
} from "@chakra-ui/react";
import React, {
  useEffect,
  useState,
  useCallback,
  LegacyRef,
  forwardRef,
} from "react";
import { FiDownloadCloud } from "react-icons/fi";
import { FaPlus } from "react-icons/fa";

import { Card } from "./Card";
import { MemberTable } from "./MemberTable";
import { Navbar } from "./Navbar";
import { Sidebar } from "./Sidebar";
import actions from "../actions";
import {
  CreateKYDEventRequest,
  CreateKYDEventRequestV2,
  KYDEvent,
} from "@common/interfaces/KYDEvent";
import { Navigate, useNavigate, Routes, Route } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDialog } from "../Common/Dialog";

enum EditModalType {
  INACTIVE,
  CREATE_EVENT,
}

enum FilterType {
  UPCOMING = "upcoming",
  PENDING_APPROVAL = "pending_approval",
  DRAFT = "draft",
  PAST = "past",
  ALL = "all",
}

const App = () => {
  const [kydEvents, setKydEvents] = useState<KYDEvent[] | null>(null);
  const [loading, setLoading] = useState<any>({});
  const [filteredEvents, setFilteredEvents] = useState<KYDEvent[]>([]);
  const [dirtyText, setDirtyText] = useState<any>({});
  const [modalType, setModalType] = useState(EditModalType.INACTIVE);
  const [filterType, setFilterType] = useState(FilterType.UPCOMING);

  const toast = useToast();
  const navigate = useNavigate();
  const dialog = useDialog();

  const updateLoading = useCallback((key: string, value: boolean) => {
    setLoading((l: any) => {
      return { ...l, [key]: value };
    });
  }, []);

  const updateDirtyText = useCallback((update: any | string) => {
    if (update === "all") {
      setDirtyText({});
    } else {
      setDirtyText((l: any) => {
        const keys = Object.keys(update);
        keys.forEach((key) => {
          if (update[key] === null) {
            delete l[key];
          } else {
            l[key] = update[key];
          }
        });
        console.log("Processed Update: ", l);
        return { ...l };
      });
    }
  }, []);

  const updateFilteredEvents = useCallback(
    (_filterType: FilterType) => {
      if (kydEvents) {
        if (_filterType === FilterType.ALL) {
          setFilteredEvents(kydEvents!);
        } else if (_filterType === FilterType.UPCOMING) {
          const events = kydEvents.filter((event) => event.status === "OPEN");
          setFilteredEvents(events);
        } else if (_filterType === FilterType.PAST) {
          const events = kydEvents.filter(
            (event) => event.status === "OFFSALE"
          );
          setFilteredEvents(events);
        } else if (_filterType === FilterType.PENDING_APPROVAL) {
          const events = kydEvents.filter(
            (event) => event.status === "PENDING_APPROVAL"
          );
          setFilteredEvents(events);
        } else if (_filterType === FilterType.DRAFT) {
          const events = kydEvents.filter((event) => event.status === "DRAFT");
          setFilteredEvents(events);
        }
      }
    },
    [setFilteredEvents, kydEvents]
  );

  useEffect(() => {
    const fetchEvents = async () => {
      updateLoading("init", true);
      try {
        const _ = await actions.ADMIN.fetchEvents();
        setKydEvents(_);
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("init", false);
    };

    fetchEvents();
  }, [updateLoading]);

  useEffect(() => {
    updateFilteredEvents(filterType);
  }, [kydEvents, updateFilteredEvents, filterType]);

  const onCreateEvent = useCallback(
    async (dText: any) => {
      updateLoading("event", true);
      try {
        const start_at = new Date(dText["event.start_at"]);
        const request: CreateKYDEventRequestV2 = {
          name: dText["event.name"],
          start_at: `${start_at.getFullYear()}-${
            start_at.getMonth() + 1
          }-${start_at.getDate()} ${start_at.getHours()}:${start_at.getMinutes()}`,
          duration: dText["event.duration"] || "1",
          venue_name: dText["event.venue_name"],
          venue_city: dText["event.venue_city"],
          display_mode: dText["event.display_mode"] || "normal",
        };

        const result = await actions.ADMIN.createEvent(request);
        console.log("Event Result: ", result);
        setModalType(EditModalType.INACTIVE);
        updateDirtyText("all");

        toast({
          title: "✅ Event Created",
          description: "Event has been created",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        navigate(`/manager/events/${result.event_id}`);
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("event", false);
    },
    [updateLoading, dialog, navigate]
  );

  const renderCreateEventModal = () => {
    const ExampleCustomInput = forwardRef(
      (
        { value, onClick }: { value?: any; onClick?: any },
        ref: LegacyRef<HTMLParagraphElement>
      ) => (
        <HStack
          cursor={"pointer"}
          h={10}
          paddingInlineStart={4}
          rounded={"md"}
          borderColor={"gray.200"}
          borderWidth={"1px"}
          onClick={onClick}
          ref={ref}
        >
          {value ? (
            <Text>{value}</Text>
          ) : (
            <Text fontWeight={"medium"} color="gray.500">
              Start Date
            </Text>
          )}
        </HStack>
      )
    );

    return (
      <Modal
        size={"xl"}
        isOpen={true}
        onClose={() => setModalType(EditModalType.INACTIVE)}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Event</ModalHeader>
          <ModalBody>
            <Stack spacing={6}>
              <Stack>
                <Text fontWeight={"medium"}>Name</Text>
                <Input
                  placeholder="Event name"
                  isDisabled={loading["event"]}
                  onChange={(e) => {
                    updateDirtyText({ "event.name": e.target.value });
                  }}
                  value={dirtyText["event.name"]}
                />
              </Stack>
              <Stack>
                <Text fontWeight={"medium"}>Event Type</Text>
                <Select
                  maxW={"200px"}
                  defaultValue={"normal"}
                  onChange={(e) =>
                    updateDirtyText({
                      [`${"event"}.display_mode`]: e.target.value,
                    })
                  }
                >
                  <option value="normal">Paid Event</option>
                  <option value="rsvp">RSVP Event</option>
                </Select>
              </Stack>
              <Stack maxW={"300px"} w="100%" spacing={0}>
                <Text fontWeight={"medium"}>Start Date</Text>
                <DatePicker
                  placeholderText="Click to select a date"
                  disabled={loading["tickettype"]}
                  dateFormat="MMMM d, yyyy h:mm aa"
                  showTimeSelect={true}
                  closeOnScroll={true}
                  minDate={new Date()}
                  customInput={<ExampleCustomInput />}
                  selected={
                    dirtyText["event.start_at"]
                      ? new Date(dirtyText["event.start_at"])
                      : null
                  }
                  onChange={(e: Date) => {
                    updateDirtyText({
                      [`event.start_at`]: e.toISOString(),
                    });
                  }}
                />
              </Stack>
              <Stack>
                <Text fontWeight={"medium"}>Duration</Text>
                <Select
                  maxW={"200px"}
                  defaultValue={"normal"}
                  onChange={(e) =>
                    updateDirtyText({
                      [`${"event"}.duration`]: e.target.value,
                    })
                  }
                >
                  <option value="1">1 hour</option>
                  <option value="1.5">1.5 hours</option>
                  <option value="2">2 hours</option>
                  <option value="2.5">2.5 hours</option>
                  <option value="3">3 hours</option>
                  <option value="3.5">3.5 hours</option>
                  <option value="4">4 hours</option>
                  <option value="4.5">4.5 hours</option>
                  <option value="5">5 hours</option>
                  <option value="5.5">5.5 hours</option>
                  <option value="6">6 hours</option>
                  <option value="12">1 day</option>
                  <option value="48">2 days</option>
                  <option value="72">3 days</option>
                </Select>
              </Stack>
              <Stack>
                <Text fontWeight={"medium"}>Venue Name</Text>
                <Input
                  placeholder="Venue name"
                  isDisabled={loading["event"]}
                  onChange={(e) => {
                    updateDirtyText({ "event.venue_name": e.target.value });
                  }}
                  value={dirtyText["event.venue_name"]}
                />
              </Stack>
              <Stack>
                <Text fontWeight={"medium"}>Venue Address</Text>
                <Input
                  placeholder="Venue address"
                  isDisabled={loading["event"]}
                  onChange={(e) => {
                    updateDirtyText({ "event.venue_city": e.target.value });
                  }}
                  value={dirtyText["event.venue_city"]}
                />
              </Stack>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button
              isDisabled={loading["event"]}
              onClick={() => {
                updateDirtyText("all");
                setModalType(EditModalType.INACTIVE);
              }}
              mr="4"
            >
              Cancel
            </Button>
            <Button
              isDisabled={
                loading.event ||
                !dirtyText["event.venue_city"] ||
                !dirtyText["event.venue_name"] ||
                !dirtyText["event.start_at"] ||
                !dirtyText["event.name"]
              }
              bg="blue.500"
              color="white"
              isLoading={loading["event"]}
              onClick={() => onCreateEvent(dirtyText)}
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

  return (
    <Stack spacing={{ base: "8", lg: "6" }}>
      {modalType === EditModalType.CREATE_EVENT
        ? renderCreateEventModal()
        : null}
      ;
      <Stack
        spacing="4"
        direction={{ base: "column", lg: "row" }}
        justify="space-between"
        align={{ base: "start", lg: "center" }}
      >
        <Stack spacing="1">
          <Heading
            size={useBreakpointValue({ base: "xs", lg: "sm" })}
            fontWeight="medium"
          >
            Events
          </Heading>
        </Stack>
      </Stack>
      {loading["init"] && (
        <VStack>
          <Spinner />
          <Text>Loading events...</Text>
        </VStack>
      )}
      {kydEvents && (
        <Stack overflowX={"scroll"} spacing={{ base: "5", lg: "6" }}>
          <HStack justify={"space-between"}>
            <Select
              maxW={"200px"}
              defaultValue={"upcoming"}
              onChange={(e) => setFilterType(e.target.value as FilterType)}
            >
              <option value="upcoming">Upcoming</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="draft">Draft</option>
              <option value="past">Past Events</option>
              <option value="all">All Events</option>
            </Select>
            <IconButton
              isDisabled={loading["event"]}
              variant={"outline"}
              aria-label="Create event"
              icon={<FaPlus />}
              onClick={() => setModalType(EditModalType.CREATE_EVENT)}
            />
          </HStack>

          <MemberTable events={filteredEvents} />
        </Stack>
      )}
    </Stack>
  );
};

export default App;
